import React, { useEffect, useState } from "react";
import { getSingleProduct, updateProduct } from "../utils/api";

const EditProductModal = ({ productId, isOpen, onClose, onProductUpdated }) => {
    const [product, setProduct] = useState({
        name: "",
        description: "",
        price: "",
        stock: "",
        category: "jordbær",
        featured: false,
    });

    const [image, setImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [bulkDiscounts, setBulkDiscounts] = useState([]);

    useEffect(() => {
        if (productId && isOpen) {
            fetchProduct();
        }
    }, [productId, isOpen]);

    const fetchProduct = async () => {
        try {
            const data = await getSingleProduct(productId);
            setProduct(data);
            setBulkDiscounts(data.bulkDiscounts || []);
        } catch (error) {
            console.error("Feil ved henting av produkt:", error);
        }
    };

    const handleAddDiscount = () => {
        setBulkDiscounts([...bulkDiscounts, { quantity: null, price: null }]);
    };
    
    const handleRemoveDiscount = (index) => {
        setBulkDiscounts(bulkDiscounts.filter((_, i) => i !== index));
    };
    
    const handleDiscountChange = (index, field, value) => {
        const updatedDiscounts = bulkDiscounts.map((discount, i) =>
            i === index ? { ...discount, [field]: value } : discount
        );
        setBulkDiscounts(updatedDiscounts);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setProduct((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleImageChange = (e) => {
        setImage(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
    
        const formData = new FormData();
        Object.keys(product).forEach((key) => {
            if (key !== "bulkDiscounts") {
                formData.append(key, product[key]);
            }
        });

        if (image) formData.append("image", image);

        if (bulkDiscounts.length > 0) {
            formData.append("bulkDiscounts", JSON.stringify(bulkDiscounts));
        } else {
            formData.append("bulkDiscounts", "[]");
        }

    
        try {
            await updateProduct(productId, formData);
            onProductUpdated("success");
            onClose();
        } catch (error) {
            onProductUpdated("error");
            console.error("Feil:", error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="h-[90%] overflow-x-auto bg-white p-6 w-[95%] md:w-full max-w-lg rounded-lg shadow-lg">
                <h2 className="text-2xl font-bold mb-4 text-gray-800">Rediger produkt</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <label className="block text-gray-700 font-medium">Produktnavn</label>
                    <input type="text" name="name" value={product.name} onChange={handleChange} className="w-full p-3 border rounded" required />

                    <label className="block text-gray-700 font-medium">Beskrivelse</label>
                    <textarea name="description" value={product.description} onChange={handleChange} className="w-full p-3 border rounded" rows="4"></textarea>

                    <label className="block text-gray-700 font-medium">Pris (NOK)</label>
                    <input type="number" name="price" value={product.price} onChange={handleChange} className="w-full p-3 border rounded" required />

                    <label className="block text-gray-700 font-medium">Lagerstatus</label>
                    <input type="number" name="stock" value={product.stock} onChange={handleChange} className="w-full p-3 border rounded" required />

                    <label className="block text-gray-700 font-medium">Kategori</label>
                    <select name="category" value={product.category} onChange={handleChange} className="w-full p-3 border rounded">
                        <option value="jordbær">Jordbær</option>
                        <option value="bringebær">Bringebær</option>
                        <option value="moreller">Moreller</option>
                    </select>

                    <label className="block text-gray-700 font-medium">Mersalg (mengderabatter)</label>
                        {bulkDiscounts.map((discount, index) => (
                            <div key={index} className="flex gap-4 items-center">
                                <input
                                    type="number"
                                    value={discount.quantity}
                                    onChange={(e) => handleDiscountChange(index, "quantity", e.target.value)}
                                    className="w-1/2 p-3 border rounded"
                                    placeholder="Antall"
                                    required
                                />
                                <input
                                    type="number"
                                    value={discount.price}
                                    onChange={(e) => handleDiscountChange(index, "price", e.target.value)}
                                    className="w-1/2 p-3 border rounded"
                                    placeholder="Totalpris"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => handleRemoveDiscount(index)}
                                    className="text-red-500 hover:text-red-700 ml-auto flex items-center justify-center p-1"
                                >
                                    ✖
                                </button>
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={handleAddDiscount}
                            className="mt-2 bg-blue-500 text-white py-2 px-4 rounded"
                        >
                            Legg til rabatt
                        </button>

                    <label className="block text-gray-700 font-medium">Last opp nytt bilde</label>
                    <input type="file" onChange={handleImageChange} className="w-full p-3 border rounded" accept="image/*" />

                    <div className="flex items-center gap-3">
                        <input type="checkbox" name="featured" checked={product.featured} onChange={handleChange} className="w-5 h-5" />
                        <label className="text-gray-700 font-medium">Utvalgt produkt</label>
                    </div>

                    <div className="flex justify-between">
                        <button type="button" onClick={onClose} className="bg-gray-500 text-white px-4 py-2 rounded">
                            Avbryt
                        </button>
                        <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded" disabled={loading}>
                            {loading ? "Lagrer..." : "Lagre endringer"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditProductModal;
